import { createNamespacedHelpers } from 'vuex';
import _get from 'lodash/get';
import apolloClient from '../../vue-apollo';
import signUp from '../../mutations/registerUser';
import signInMutation from '../../mutations/signIn';
import fetchUser from '../../queries/fetchUser';
import { getContextTMC } from '@/utils/tmc';
import updateUser from '../../mutations/updateUser';
import confirmUser from '../../mutations/confirmUser';
import validateUser from '../../mutations/validateUser';
import unsubscribe from '../../mutations/unsubscribe';
import resubscribe from '../../mutations/resubscribe';
import unsubscribeChallenge from '../../queries/unsubscribeChallenge';
import { solveChallenge, encodePayload } from '@/utils/altcha';
import resendVerificationCode from '../../mutations/resendVerificationCode';
import { getEncryptedValue, getDecryptedValue } from '@/utils/crypto';
import setUserPropertiesToAmplitude from '@/mutations/setUserPropertiesToAmplitude';
export const namespacedHelper = createNamespacedHelpers('user');

/**
 * Why a subscription change did not happen. `NO_USER` means we never learned
 * whose subscription this is (fetchUser refused or failed), so retrying cannot
 * help; `FAILED` covers a network error or a response that did not confirm
 * success, which a retry may fix.
 */
export const SUBSCRIPTION_NO_USER = 'NO_USER';
export const SUBSCRIPTION_FAILED = 'FAILED';

export class SubscriptionError extends Error {
  constructor(reason, cause) {
    super(`subscription change failed: ${reason}`);
    this.reason = reason;
    this.cause = cause;
  }
}

/**
 * Solve a fresh human-check challenge (see utils/altcha.js). Never throws:
 * null means "send without an answer" and marvin-core decides - accepted while
 * its unsubscribe.altcha.required flag is off, refused once it is on.
 */
export async function fetchAltchaPayload(apollo) {
  try {
    const response = await unsubscribeChallenge({ apollo });
    const challenge = _get(response, ['data', 'unsubscribeChallenge']);
    if (!challenge) return null;
    const number = await solveChallenge(challenge);
    return number === null ? null : encodePayload(challenge, number);
  } catch (error) {
    console.log('human check could not be solved', error);
    return null;
  }
}

export function changeSubscription(
  send,
  field,
  getters,
  { getAltcha = fetchAltchaPayload } = {}
) {
  const email = getters.userEmail;
  if (!email) {
    return Promise.reject(new SubscriptionError(SUBSCRIPTION_NO_USER));
  }
  const apollo = apolloClient.defaultClient;
  return Promise.resolve()
    .then(() => getAltcha(apollo))
    .catch(() => null)
    .then(altcha =>
      send({
        apollo,
        email,
        tmc: getters.getTmc,
        altcha
      })
    )
    .catch(error => {
      throw new SubscriptionError(SUBSCRIPTION_FAILED, error);
    })
    .then(response => {
      const result = _get(response, ['data', field]);
      if (!result || result.success !== true) {
        throw new SubscriptionError(SUBSCRIPTION_FAILED, result);
      }
      return result;
    });
}

const state = {
  user: null,
  isAuth: true,
  isPlusAccess: true,
  validateError: null,
  corporationId: null,
  reference: null,
  activeTripId: null,
  destinationUnsubscribe: null,
  comingFromEmailML: false,
  tmc: null,
  latitude: null,
  longitude: null,
  accountNumber: null,
  emailId: null
};

const getters = {
  user: state => {
    return getDecryptedValue(state.user);
  },
  userEmail: state => {
    // getDecryptedValue yields undefined for state this key cannot decrypt
    return state.user && (getDecryptedValue(state.user) || {}).email;
  },
  emailId: state => {
    return state.emailId;
  },
  userId: state => {
    return state.user && (getDecryptedValue(state.user) || {})['userId'];
  },
  corporationId: state => {
    return getDecryptedValue(state.corporationId);
  },
  accountNumber: state => {
    if (getContextTMC() == 'gant') {
      return getDecryptedValue(state.accountNumber);
    } else {
      return '';
    }
  },
  activeTripId: state => {
    return getDecryptedValue(state.activeTripId);
  },
  comingFromEmailML: state => {
    return state.comingFromEmailML;
  },
  getReference(state) {
    return getDecryptedValue(state.reference);
  },
  isAuth: state => {
    return state.user != undefined;
  },
  isPlusAccess: state => {
    return state.isPlusAccess;
  },
  getValidateError: state => {
    return state.validateError;
  },
  getDestinationUnsubscribe: state => {
    return state.destinationUnsubscribe;
  },
  getTmc: state => {
    // state.tmc is null until a uid link has been resolved; reading .tmc off it
    // threw inside the unsubscribe action, which the page never saw.
    return (state.tmc && state.tmc.tmc) || getContextTMC();
  },
  userPasswordRules: () => ({
    required: value => !!value || 'Password is required',
    length: value =>
      value.length >= 8 || 'Password should be a minimum of eight characters',
    password: value => {
      const pattern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
      return (
        pattern.test(value) ||
        'Password should have at least one upper case, one lower case and one number'
      );
    }
  }),
  getLocationCoordinates: state => {
    if (state.latitude == null || state.longitude == null) {
      return null;
    } else {
      return {
        latitude: state.latitude,
        longitude: state.longitude
      };
    }
  }
};

const mutations = {
  signIn(state, form) {
    signInMutation({
      apollo: apolloClient.defaultClient,
      ...form
    })
      .then(response => _get(response, 'data.signIn', {}))
      .then(response => {
        state.user = getEncryptedValue(response.user);
      });
  },
  updateUser(state, user) {
    state.user = user;
  },
  clearUser(state) {
    state.user = null;
  },
  registerUser(state, form) {
    signUp({
      apollo: apolloClient.defaultClient,
      ...form
    })
      .then(response => _get(response, 'data.registerUser', {}))
      .then(response => {
        state.user = getEncryptedValue(response.user);
      });
  },
  updateUserAttr(state, value) {
    updateUser({
      apollo: apolloClient.defaultClient,
      userId: (getters.user(state) || {}).userId || '',
      userModifications: value
    })
      .then(response => _get(response, 'data.updateUser', {}))
      .then(response => {
        state.user = getEncryptedValue(response.user);
      });
  },
  setValidateError(state, value) {
    state.validateError = value;
  },
  updateCorporationId(state, value) {
    state.corporationId = getEncryptedValue(value);
  },
  updateEmailId(state, value) {
    state.emailId = value;
  },
  updateAccountNumber(state, value) {
    state.accountNumber = getEncryptedValue(value);
  },
  updateActiveTripId(state, value) {
    state.activeTripId = getEncryptedValue(value);
  },
  updateComingFromEmailML(state, value) {
    state.comingFromEmailML = value;
  },
  updateReference(state, payload) {
    state.reference = getEncryptedValue(payload);
  },
  updateDestinationUnsubscribe(state, value) {
    state.destinationUnsubscribe = value;
  },
  setTmcValue(state, value) {
    state.tmc = value;
  },
  updateUserCoordinates(state, value) {
    state.latitude = value.latitude;
    state.longitude = value.longitude;
  }
};

const actions = {
  signOut(context) {
    context.commit('clearUser');
  },
  signIn(context, user) {
    context.commit('signIn', user);
  },
  setUser({ commit }, user) {
    commit('updateUser', getEncryptedValue(user));
    const encoded = btoa(
      unescape(
        encodeURIComponent(
          JSON.stringify({
            user_email: user && user.email
          })
        )
      )
    );
    setUserPropertiesToAmplitude({
      apollo: apolloClient.defaultClient,
      json: encoded
    })
      .then(response => _get(response, 'data.setUserPropertiesToAmplitude', {}))
      .catch(error => console.log(error));
  },

  setUserWithTmc({ commit }, userAndTmcId) {
    let tmcByTripId = userAndTmcId['tmcByTripId'];
    if (tmcByTripId != getContextTMC()) {
      window.location.href = 'https://www.grapevine.travel/gv_not_found.html';
    }
    let user = userAndTmcId['fetchUser'];
    commit('updateUser', getEncryptedValue(user));
    const encoded = btoa(
      unescape(
        encodeURIComponent(
          JSON.stringify({
            user_email: user && user.email
          })
        )
      )
    );
    setUserPropertiesToAmplitude({
      apollo: apolloClient.defaultClient,
      json: encoded
    })
      .then(response => _get(response, 'data.setUserPropertiesToAmplitude', {}))
      .catch(error => console.log(error));
  },
  setTmc({ commit }, tmc) {
    commit('setTmcValue', tmc);
  },
  setTestUser({ commit }) {
    commit(
      'updateUser',
      getEncryptedValue({
        firstName: 'Test',
        lastName: 'Test',
        email: 'test_user@moethennessy.com',
        userId: process.env.VUE_APP_TEST_USER_UID,
        userStatus: 'verified'
      })
    );
  },
  addAccount() {
    console.log('add account');
  },
  removeAccount(id) {
    console.log(`remove account ${id}`);
  },
  setNewFirstName(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "first_name":"${value}" }`));
  },
  setNewLastName(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "last_name":"${value}" }`));
  },
  setNewEmail(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "email":"${value}" }`));
  },
  setNewPassword(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "password":"${value}" }`));
  },
  setNewPhone(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "phone":"${value}" }`));
  },
  setPhotoUrl(context, value) {
    context.commit('updateUserAttr', JSON.parse(`{ "photoUrl":"${value}" }`));
  },
  clearUser(context) {
    context.commit('clearUser');
  },
  fetchUser({ dispatch }, uId, tripId, emailId) {
    return fetchUser({
      apollo: apolloClient.defaultClient,
      uId: uId,
      tripId: tripId,
      emailId: emailId
    })
      .then(response => _get(response, 'data', {}))
      .then(response => {
        dispatch('setUserWithTmc', response);
      });
  },
  async confirmUser({ dispatch }, args) {
    return confirmUser({
      apollo: apolloClient.defaultClient,
      ...args
    })
      .then(response => _get(response, 'data.confirmUser', {}))
      .then(response => {
        dispatch('setUser', response.user);
      });
  },
  validateUser({ getters, commit, dispatch }, args) {
    return validateUser({
      apollo: apolloClient.defaultClient,
      ...args
    })
      .then(response => _get(response, 'data.validateUser', {}))
      .then(response => {
        if (response.errors) {
          commit('setValidateError', response.errors[0]);
          return false;
        }

        if (response.success && getters.userEmail === response.user.email) {
          dispatch('setUser', response.user);
          return response;
        }
      });
  },
  async resendVerificationCode({ getters, commit }) {
    return resendVerificationCode({
      apollo: apolloClient.defaultClient,
      uId: getters.userId
    })
      .then(response => _get(response, 'data.resendVerificationCode', {}))
      .then(response => {
        if (response.errors) {
          commit('setValidateError', response.errors[0]);
          return false;
        }

        return response.success;
      });
  },
  setCorporationId(context, value) {
    context.commit('updateCorporationId', value);
  },
  setEmailId(context, value) {
    context.commit('updateEmailId', value);
  },
  setAccountNumber(context, value) {
    context.commit('updateAccountNumber', value);
  },
  setActiveTripId(context, value) {
    context.commit('updateActiveTripId', value);
  },
  setComingFromEmailML(context, value) {
    context.commit('updateComingFromEmailML', value);
  },
  setReference({ commit }, value) {
    commit('updateReference', value);
  },
  setDestinationUnsubscribe(context, value) {
    context.commit('updateDestinationUnsubscribe', value);
  },
  // Both resolve ONLY on a confirmed success and reject otherwise, so the page
  // can never announce a result it did not get. They used to resolve with {}
  // on a missing email or an error payload, and the page said "You have
  // unsubscribed" either way.
  unsubscribe({ getters }) {
    return changeSubscription(unsubscribe, 'unsubscribe', getters);
  },
  resubscribe({ getters }) {
    return changeSubscription(resubscribe, 'resubscribe', getters);
  },
  setUserGeoLocation({ commit }, value) {
    commit('updateUserCoordinates', value);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
